import AssignmentIcon from '@mui/icons-material/Assignment';
import { Box, Button } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { LessonPlanRole, LessonPlanSessionModel } from '../object-actions/types/types';

/** Returns true when every session in the plan through throughSessionNumber is completed.

Lesson plans are not always contiguous (e.g. session 5 may be absent); only sessions
that exist in `sessions` are required — we cannot complete a session that was never generated.
*/
export function areSessionsCompletedUpTo(
  sessions: LessonPlanSessionModel[],
  throughSessionNumber: number
): boolean {
  if (!sessions.length || throughSessionNumber < 1) return false;

  const relevant = sessions.filter(
    (session) =>
      session.session_number >= 1 && session.session_number <= throughSessionNumber
  );
  if (relevant.length === 0) return false;

  return relevant.every((session) => Boolean(session.completed_at));
}

export interface ProgressSurveyCtaProps {
  lessonPlanId: number;
  weekNum: 4 | 8 | 12;
  sessions: LessonPlanSessionModel[];
  /** Progress surveys are for coach, parent, or payer — not the athlete. */
  myRoles?: LessonPlanRole[];
}

const WEEK_LABELS: Record<4 | 8 | 12, string> = {
  4: 'Week 4',
  8: 'Week 8',
  12: 'Week 12',
};

const ProgressSurveyCta: React.FC<ProgressSurveyCtaProps> = ({ lessonPlanId, weekNum, sessions, myRoles }) => {
  const canTakeProgressSurvey =
    myRoles?.includes('coach') || myRoles?.includes('parent') || myRoles?.includes('payer');

  const isVisible =
    canTakeProgressSurvey && areSessionsCompletedUpTo(sessions, weekNum);

  if (!isVisible) {
    return null;
  }

  const href = `/lesson-plan/${lessonPlanId}/week/${weekNum}/progress-survey`;
  const label = weekNum === 12 ? 'Take final Progress Survey' : `Take week ${weekNum} Progress Survey`;

  return (
    <Box
      data-attr={`progress-survey-cta_week-${weekNum}`}
      data-testid={`progress-survey-cta--week-${weekNum}`}
    >
      <Button
        component={Link}
        to={href}
        variant="contained"
        color="primary"
        size="medium"
        startIcon={<AssignmentIcon />}
        data-attr="progress-survey-cta_button"
      >
        {label}
      </Button>
    </Box>
  );
};

export default ProgressSurveyCta;
